import React, { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Formule de Cockcroft-Gault. Stades KDIGO pour la classification.
const stage = (crcl, t) => {
  if (crcl >= 90) return { label: t('crcl_stage_g1'), color: '#10B981' };
  if (crcl >= 60) return { label: t('crcl_stage_g2'), color: '#10B981' };
  if (crcl >= 45) return { label: t('crcl_stage_g3a'), color: '#F59E0B' };
  if (crcl >= 30) return { label: t('crcl_stage_g3b'), color: '#F59E0B' };
  if (crcl >= 15) return { label: t('crcl_stage_g4'), color: '#EF4444' };
  return { label: t('crcl_stage_g5'), color: '#EF4444' };
};

const CreatinineClearance = () => {
  const { t } = useLanguage();
  const [sex, setSex] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [creatinine, setCreatinine] = useState('');
  const [unit, setUnit] = useState('umol');

  const a = parseFloat(age);
  const w = parseFloat(weight);
  const cr = parseFloat(creatinine);
  const valid = sex && a > 0 && w > 0 && cr > 0;

  let crcl = null;
  if (valid) {
    const crMgdl = unit === 'umol' ? cr / 88.4 : cr;
    crcl = ((140 - a) * w) / (72 * crMgdl);
    if (sex === 'femme') crcl *= 0.85;
  }
  const result = crcl !== null && crcl > 0 && crcl < 250 ? crcl : null;
  const cat = result !== null ? stage(result, t) : null;

  return (
    <ToolPageLayout
      title={t('crcl_title')}
      subtitle={t('crcl_subtitle')}
      specialty="nephrologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <FlaskConical className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('crcl_info')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('cvrisk_sex_label')}</Label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            <option value="">{t('cvrisk_sex_placeholder')}</option>
            <option value="homme">{t('cvrisk_sex_male')}</option>
            <option value="femme">{t('cvrisk_sex_female')}</option>
          </select>
        </div>
        <div>
          <Label className="mb-1 block">{t('cvrisk_age_label')}</Label>
          <Input type="number" min="18" max="120" value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('bmi_weight_label')}</Label>
          <Input type="number" min="30" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('crcl_creatinine_label')}</Label>
          <div className="flex gap-2">
            <Input type="number" min="1" step="0.1" value={creatinine} onChange={(e) => setCreatinine(e.target.value)} placeholder={unit === 'umol' ? 'Ex: 80' : 'Ex: 0.9'} className="rounded-xl flex-1" />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="border border-[#E2E8F0] rounded-xl px-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
            >
              <option value="umol">µmol/L</option>
              <option value="mgdl">mg/dL</option>
            </select>
          </div>
        </div>
      </div>

      {cat && (
        <div className="rounded-2xl p-6 text-center border" style={{ borderColor: `${cat.color}40`, backgroundColor: `${cat.color}0D` }}>
          <div className="text-sm text-[#64748B] mb-1">{t('crcl_result_label')}</div>
          <div className="text-3xl font-bold" style={{ color: cat.color }}>{result.toFixed(0)} ml/min</div>
          <div className="mt-2 font-semibold" style={{ color: cat.color }}>{cat.label}</div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('crcl_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default CreatinineClearance;
